import { Quotes, UsersThree, ShieldCheck, Handshake } from "@phosphor-icons/react";
import { SmartImage } from "@/components/SmartImage";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const FOUNDER_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_rk-jain-premium/artifacts/661j9xu6_RK%20jain%20founder.png";

const PILLAR_ICONS = [UsersThree, ShieldCheck, Handshake];

export const FounderSection = () => {
  const { t } = useLang();
  const pillars = t("founder.pillars");

  return (
    <section
      className="relative overflow-hidden bg-champagne py-20 md:py-28"
      data-testid="founder-section"
    >
      <div className="pointer-events-none absolute right-0 top-1/2 h-[520px] w-[520px] -translate-y-1/2 translate-x-1/4 rounded-full bg-gold/20 blur-3xl" />

      <div className="container-x relative grid items-center gap-16 lg:grid-cols-2">
        {/* Text */}
        <Reveal>
          <p className="overline mb-5">{t("founder.eyebrow")}</p>
          <h2 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-forest md:text-6xl">
            {t("founder.l1")}<br />
            {t("founder.l2")}<br />
            <span className="text-gold">{t("founder.l3")}</span>
          </h2>
          <div className="mt-6 h-1 w-16 bg-gold" />
          <p className="mt-7 max-w-md text-lg leading-relaxed text-ink/75">
            {t("founder.intro")}
          </p>

          <p className="mt-6 font-serif text-2xl italic text-forest md:text-3xl">
            &ldquo;{t("founder.philosophy")}&rdquo;
          </p>

          <div className="mt-10 space-y-6">
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <div key={i} className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-hairline bg-surface text-forest shadow-sm">
                    <Icon size={24} weight="thin" />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-forest">{p.title}</h3>
                    <p className="text-sm text-subtle">{p.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* Portrait */}
        <Reveal delay={0.1} className="relative">
          <div className="relative mx-auto max-w-md">
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-gold/30 via-gold/10 to-transparent blur-2xl" />
            <div className="absolute -right-3 -top-3 z-20 h-16 w-16 rounded-tr-2xl border-r-2 border-t-2 border-gold" />
            <div className="absolute -bottom-3 -left-3 z-20 h-16 w-16 rounded-bl-2xl border-b-2 border-l-2 border-gold" />

            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-champagne shadow-2xl shadow-forest/15 ring-1 ring-hairline">
              <SmartImage
                src={FOUNDER_IMG}
                alt={t("founder.name")}
                className="absolute inset-0 h-full w-full object-cover"
                style={{ transform: "scale(2.15)", transformOrigin: "80% 30%" }}
              />
            </div>

            <div className="relative z-20 mx-4 -mt-14 rounded-2xl border border-hairline bg-surface p-6 shadow-xl shadow-forest/10">
              <Quotes size={28} weight="fill" className="text-gold" />
              <p className="mt-2 font-serif text-base italic leading-snug text-ink md:text-lg">
                {t("founder.quote")}
              </p>
              <div className="mt-4 border-t border-hairline pt-3">
                <p className="font-heading text-lg font-bold text-forest">{t("founder.name")}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-gold">{t("founder.role")}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default FounderSection;
